"use client";

/**
 * Organization Hooks
 *
 * Abstraction layer for organization management.
 * Components should use these instead of directly importing from auth providers.
 *
 * Architecture Rule: AU-01 - Auth Abstraction Layer
 *
 * Migration Note: This file previously used Clerk organization hooks.
 * Now uses Supabase Auth with Prisma OrganizationMembership model.
 * The public API remains the same for backward compatibility.
 */
import { useSupabaseAuth } from "@/components/auth/supabase-auth-provider";

// ============================================================================
// Types - Maintaining backward compatibility with Clerk types
// ============================================================================

export interface OrganizationMembership {
  organization: {
    id: string;
    name: string;
    imageUrl?: string;
    publicMetadata?: Record<string, unknown>;
  };
}

export interface OrganizationListContext {
  // Loading state
  isLoaded: boolean;

  // Memberships
  userMemberships: OrganizationMembership[];

  // Actions
  setActive: (params: { organization: string }) => Promise<void>;
}

export interface CurrentOrganizationContext {
  // Loading state
  isLoaded: boolean;

  // Organization data
  organization: {
    id: string;
    name: string;
    slug?: string;
    imageUrl?: string;
    publicMetadata?: Record<string, unknown>;
  } | null;
  organizationId: string | null;
  organizationName: string | null;

  // Membership data
  membership: {
    role: string;
  } | null;
  membershipRole: string | null;
}

export interface OrganizationUserContext {
  // Loading state
  isLoaded: boolean;

  // User data
  user: {
    id: string;
    primaryEmailAddress?: { emailAddress: string };
    firstName?: string | null;
    lastName?: string | null;
    fullName?: string | null;
    imageUrl?: string;
  } | null;
  userId: string | null;
}

// ============================================================================
// Hook Implementations
// ============================================================================

/**
 * Hook for organization list and switching
 * Use in organization switcher components
 */
export function useOrganizationListContext(): OrganizationListContext {
  const auth = useSupabaseAuth();

  const memberships: OrganizationMembership[] = auth.organizations.map((org) => ({
    organization: {
      id: org.id,
      name: org.name,
      imageUrl: undefined, // Supabase doesn't have built-in org images
      publicMetadata: {
        type: org.type,
        slug: org.slug,
      },
    },
  }));

  return {
    isLoaded: auth.isLoaded,
    userMemberships: memberships,
    setActive: async (params) => {
      await auth.setActiveOrganization(params.organization);
    },
  };
}

/**
 * Hook for current organization data
 * Use when you need info about the current organization
 */
export function useCurrentOrganization(): CurrentOrganizationContext {
  const auth = useSupabaseAuth();
  const activeOrg = auth.activeOrganization;

  const organization = activeOrg
    ? {
        id: activeOrg.id,
        name: activeOrg.name,
        slug: activeOrg.slug,
        imageUrl: undefined,
        publicMetadata: {
          type: activeOrg.type,
        },
      }
    : null;

  const membership = activeOrg
    ? {
        role: activeOrg.role,
      }
    : null;

  return {
    isLoaded: auth.isLoaded,
    organization,
    organizationId: activeOrg?.id ?? null,
    organizationName: activeOrg?.name ?? null,
    membership,
    membershipRole: activeOrg?.role ?? null,
  };
}

/**
 * Hook for user data in organization context
 * Use when you need user info alongside organization data
 */
export function useOrganizationUser(): OrganizationUserContext {
  const auth = useSupabaseAuth();

  const nameParts = auth.user?.name?.split(" ") ?? [];
  const firstName = nameParts[0] ?? null;
  const lastName = nameParts.slice(1).join(" ") || null;

  const user = auth.user
    ? {
        id: auth.user.id,
        primaryEmailAddress: { emailAddress: auth.user.email },
        firstName,
        lastName,
        fullName: auth.user.name,
        imageUrl: undefined,
      }
    : null;

  return {
    isLoaded: auth.isLoaded,
    user,
    userId: auth.user?.id ?? null,
  };
}

/**
 * Combined hook for organization switcher functionality
 * Combines organization list, current org, and user data
 */
export function useOrganizationSwitcher(): {
  isLoaded: boolean;
  currentOrgId: string | null;
  userMemberships: OrganizationMembership[];
  setActive: (params: { organization: string }) => Promise<void>;
  user: OrganizationUserContext["user"];
} {
  const auth = useSupabaseAuth();

  const memberships: OrganizationMembership[] = auth.organizations.map((org) => ({
    organization: {
      id: org.id,
      name: org.name,
      imageUrl: undefined,
      publicMetadata: {
        type: org.type,
        slug: org.slug,
      },
    },
  }));

  const nameParts = auth.user?.name?.split(" ") ?? [];
  const firstName = nameParts[0] ?? null;
  const lastName = nameParts.slice(1).join(" ") || null;

  const user = auth.user
    ? {
        id: auth.user.id,
        primaryEmailAddress: { emailAddress: auth.user.email },
        firstName,
        lastName,
        fullName: auth.user.name,
        imageUrl: undefined,
      }
    : null;

  return {
    isLoaded: auth.isLoaded,
    currentOrgId: auth.activeOrganizationId,
    userMemberships: memberships,
    setActive: async (params) => {
      await auth.setActiveOrganization(params.organization);
    },
    user,
  };
}
